import React, { FC, ReactElement, useState, useEffect, useMemo } from 'react';
import AppFooter from './AppFooter';
import { AppHeader } from './AppHeader';
import Container from 'saur-design-sys-web/lib/Atoms/Container/Container';
import { css } from 'styled-components';
import { useLocation } from 'react-router-dom'
import { ThemeType } from 'saur-design-sys-web/lib/BrandsConfig';
import {
  device,
  onlyForDesktop,
  onlyForMobile,
} from 'saur-design-sys-web/lib/Theme';
import { Brands } from 'saur-design-sys-web/lib/BrandsConfig';
import MobileMenu from '../components/MobileMenu';
import { useSelector, useDispatch } from 'react-redux';
import { States } from 'saur-states-management';
import { isFeatureEnabled, getLanguage } from 'saur-states-management/lib/selectors/settingsSelectors';
import { getAccessToken } from 'saur-states-management/lib/selectors/authSelectors';
import { deliveryPointActions } from 'saur-states-management/lib/states/deliveryPointState';
import { usePath } from '../common/navigation';
import { getAuthenticatedUser } from 'saur-states-management/lib/selectors/authSelectors';
import { authActions } from 'saur-states-management/lib/states/authState';
import { getClientAccountsState } from 'saur-states-management/lib/selectors/clientAccountsSelector';
import useEventListener from '../common/useEventListener';
import { getConsumer } from 'saur-states-management/lib/selectors/consumerSelector';
import Consumer from 'saur-states-management/lib/models/consumer';

type ContentLoggedWrapperProps = {
  children: ReactElement;
  isDashboard: boolean
};

type modalMode = 'in' | 'out' | undefined;

const ContentLoggedWrapper: FC<ContentLoggedWrapperProps> = ({ children, isDashboard }) => {
  const [visibleMobileMenu, setVisibleMobileMenu] = React.useState<modalMode>(
    undefined
  );
  const [subTitle, setSubtitle] = useState<'clientRef' | 'address'>('clientRef');
  const [showActionButtons, setShowActionButtons] = useState<'show' | 'hide'>('hide');
  const [showContractSection, setShowContractSection] = useState<'show' | 'hide'>('hide');
  const [showSectionSubscription, setShowSectionSubscription] = useState<'show' | 'hide'>('show');
  const [contractWidgetRef, setContractWidgetRef] = useState<Element | null>(null);
  const [eventListenerExist, setEventListenerExist] = useState<boolean>(false);

  const { getPath } = usePath();
  const location = useLocation();
  const state = useSelector((s: States) => s);
  const currentVersion = isFeatureEnabled(state, 'Portail-Professionnel') ? 'v2' : 'v1';
  const clientAccountsState = useSelector(getClientAccountsState);
  const isMultiContrats = useSelector((s: States) => isFeatureEnabled(s, 'Contrat-MultiContrats'))
  const isInterventionHistorique = useSelector((s: States) => isFeatureEnabled(s, 'Intervention-Afficher-Historique'))
  const authenticatedUser = useSelector(getAuthenticatedUser);
  const isFamilyMode = authenticatedUser?.isFamilyMode || false
  const accessToken = useSelector(getAccessToken) as string;
  const dispatch = useDispatch();
  const settingState = useSelector((s: States) => s.settings);
  const currentLang = useSelector(getLanguage) as string;
  const consumerIsContractTerminated = authenticatedUser?.defaultContract?.isContractTerminated;
  const theme = useSelector((s: States) => s.settings.theme) as Brands;
  const consumer = useSelector(getConsumer) as Consumer;


  useEffect(() => {
    setContractWidgetRef(document.querySelector('contract-widget'))
  }, []);

  useEventListener(
    'select-family-on-click-event',
    (e) => {
      const selectFamilyEvent = e as CustomEvent;
      if (selectFamilyEvent.detail.sectionSubscriptionIds)
        dispatch(authActions.setAuthentifiedUserFamily(selectFamilyEvent.detail));
    },
    contractWidgetRef
  );

  useEffect(() => {
    if (location.pathname.includes(getPath('dashboard')) || location.pathname.includes(getPath('profile'))) {
      setSubtitle('address');
      setShowActionButtons('hide');
      setShowContractSection('show');
      setShowSectionSubscription('show');
    }
    else if (location.pathname.includes(getPath('myContract'))) {
      setSubtitle('clientRef');
      setShowActionButtons('show');
      setShowContractSection('show');
      setShowSectionSubscription('show');
    }
    else {
      setSubtitle('clientRef');
      setShowActionButtons('hide');
      setShowContractSection('hide');
      setShowSectionSubscription('show');
    }

    if (location.pathname.includes(getPath('dashboard')) && isFamilyMode) {
      setShowSectionSubscription('hide');
      setShowActionButtons('hide');
      setShowContractSection('hide');
    }

  }, [location, getPath, clientAccountsState, isFamilyMode])


  function toggleMenu(option: modalMode) {
    setVisibleMobileMenu(option);
  }

  function removeVisibleMobileMenu() {
    setVisibleMobileMenu('out');
  }

  const widgetsList = useMemo(() => [
    'create-account-widget',
    'overconsuming-alerts-list',
    'forget-password-widget',
    'user-information-widget',
    'update-email-result-widget',
    'login-widget',
    'widget-exchanges',
    'coachconso-widget',
    'widget-articles',
    'header-payments',
    'next-payment',
    'invoice-history',
    'payments-history',
    'my-services',
    'last-invoice-widget',
    'last-meter-widget',
    'consumption-graph',
    'widget-delivery-info',
    'contact-us-widget',
    'contract-widget',
    'contact-widget',
    'widget-exchanges-switch'], []);

  useEffect(() => {
    if (authenticatedUser) {
      widgetsList.forEach(widget => {
        let ref: any = document.querySelector(widget);
        if (ref) {
          ref.sectionsubscriptionid = authenticatedUser.defaultSectionId;
          ref.sectionsubscriptionids = authenticatedUser.defaultSectionIds;
          ref.customeraccountid = authenticatedUser.defaultCustomerAccountId;
          ref.refclient = authenticatedUser.clientReference;
          ref.clientid = authenticatedUser.clientId;
          ref.clientreference = authenticatedUser.clientReference;
          ref.clientfirstname = consumer?.firstName ?? ''
          ref.familymode = authenticatedUser.isFamilyMode
          ref.familyid = authenticatedUser.defaultFamilyId
        }
      });
    }
  }, [widgetsList, authenticatedUser?.firstName, authenticatedUser?.isFamilyMode, consumer?.firstName])

  useEffect(() => {

    function updateSelectedContract(e: any) {
      if (e?.detail?.clientId && e?.detail?.clientReference && e?.detail?.customerAccountId && e?.detail?.sectionSubscriptionId) {
        dispatch(authActions.setAuthentifiedUserInformations(e.detail));
        if (isInterventionHistorique && !e.detail.isContractTerminated) {
          dispatch(deliveryPointActions.fetchClientWorkOrder(e.detail.sectionSubscriptionId))
        }
      }
    }
    const component = document.querySelector('contract-widget');
    if (component && !eventListenerExist) {
      component.addEventListener('contract-clicked', (e) => {
        updateSelectedContract(e)
      });
      setEventListenerExist(true);
    }
  }, [eventListenerExist, authenticatedUser, dispatch, isInterventionHistorique, consumerIsContractTerminated]);


  return (
    <Container id='logged-content-wrapper' className={`content-logged-wrapper theme-${theme}`}>
      <AppHeader
        toggleMenu={toggleMenu}
        menuStatus={visibleMobileMenu}
        showClientProMenu={isFamilyMode}
      />

      <Container
        className='clw-main-content-row'
        onClick={removeVisibleMobileMenu}
      >
        <Container className={`clw-main-content ${isDashboard ? 'dashboard' : ''}`}>
          <Container className='clw-content'>
            {isMultiContrats && (
              <Container className='multiContractContainer'>
                <contract-widget
                  showcontractsection={showContractSection}
                  showmodal='hide'
                  showactionbuttons={showActionButtons}
                  subTitle={subTitle}
                  token={accessToken}
                  lang={currentLang}
                  theme={settingState.theme}
                  currentversion={currentVersion}
                  showsectionsubscription={showSectionSubscription}
                />
              </Container>
            )}
            {children}</Container>
        </Container>
      </Container>
      <AppFooter />
      <MobileMenu toggleMenu={toggleMenu} visible={visibleMobileMenu} />
    </Container>
  );
};

export const ContentLoggedWrapperStyles = css`
  .content-logged-wrapper {
    .multiContractContainer {
      margin-bottom: 20px
    }
    .clw-background-wave {
      ${onlyForDesktop}
      width: 100%;
      position: absolute;
      bottom: 0;
      left: 0;
      right: 0;
    }
    .clw-background-wave-mobile {
      ${onlyForMobile}
      width: 100%;
      position: absolute;
      bottom: 0;
    }
    .clw-main-content-row {
      position: relative;
      flex: 1;
      flex-grow: 1;
      background-color: ${(props: ThemeType) => props.theme.colors.primaryLight};
      min-height: 100vh;
      @media ${device.mobile} {
        padding-top: 64px;
      }
      @media ${device.tablet} {
        padding-top: 90px;
      }
    }
    .clw-content {
      width: calc(100% - 40px);
      @media ${device.mobile} {
        padding: 30px 20px 20px 20px;
        align-self: auto;
      }
      @media ${device.tablet} {
        padding: 30px 20px 20px 20px;
        max-width: 1063px;
        align-self: auto;
      }
      @media ${device.desktop} {
        align-self: center;
      }
    }
    .clw-main-content {
      background-color: ${(props: ThemeType) =>
    props.theme.colors.primaryLight};
      min-height: 100vh;
      @media ${device.mobile} {
        border-top-left-radius: 0;
        background: url('/assets/bgWave-consumption-mobile.svg') no-repeat;
        background-position: bottom;
      }
      @media ${device.tablet} {
        border-top-left-radius: 60px;
        background: url('/assets/bgWave-consumption-desktop.svg') no-repeat;
        background-position: bottom;
      }
      &.dashboard {
        @media ${device.mobile} {
          border-top-left-radius: 0;
          background: url('/assets/wave-mobile.svg') no-repeat;
          background-position: bottom;
        }
        @media ${device.tablet} {
          border-top-left-radius: 60px;
          background: url('/assets/wave-desktop.svg') no-repeat;
          background-position: bottom;
        }
      }
    }
  }
`;

export default ContentLoggedWrapper;
